/**
 * useSentinelDates — which Sentinel-2 acquisitions exist over the current view.
 *
 * The renderer (DEA) and the catalogue (Earth Search) are deliberately different
 * providers so they fail independently: DEA's own STAC was returning 503 while
 * this was designed, and DEA's WMS time dimension is national and carries no
 * cloud cover, which is exactly what the UI needs to be useful.
 *
 * Earth Search is keyless, CORS-open and answers in ~0.6s.
 * https://earth-search.aws.element84.com/v1/search
 *
 * M1 uses `latestClear` only. M2 uses the full `dates` array for the time bar.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const STAC_URL = 'https://earth-search.aws.element84.com/v1/search';
const DEFAULT_COLLECTION = 'sentinel-2-l2a';

/** Cloud cover (%) at or below which a date counts as "clear". */
export const DEFAULT_MAX_CLOUD = 20;

/** How far back to look. The time bar lets the user widen this. */
export const DEFAULT_LOOKBACK_DAYS = 730;

/** Ranges offered by the time bar. `null` = the whole archive. */
export const DATE_RANGES = [
  { id: '6m', label: '6 months', days: 183 },
  { id: '2y', label: '2 years', days: 730 },
  { id: 'all', label: 'Full archive', days: null },
];

/**
 * Sentinel-2 over Australia starts here (verified against DEA's time
 * dimension). Landsat reaches back to 1986-08-16 and is passed in explicitly.
 */
export const ARCHIVE_START = '2015-07-12T00:00:00Z';

/**
 * Cap on paged requests. The full archive over one district is ~3000 scenes;
 * at 250 per page that is 12 requests of ~0.6s. Beyond the cap the bar says so
 * rather than pretending the list is complete.
 */
const MAX_PAGES = 14;
const PAGE_SIZE = 250;

/**
 * A focus window is one year over one district (~700 scenes). Six pages is
 * headroom over that, and it exists so jumping to an old year costs a bounded
 * query instead of raising MAX_PAGES and making every pan more expensive.
 */
const FOCUS_MAX_PAGES = 6;

/** Rounding applied to the bbox before it becomes a cache key. */
const BBOX_PRECISION = 0.25;

const CACHE_TTL_MS = 10 * 60 * 1000;
const CACHE_MAX_ENTRIES = 40;

/**
 * Same shape as utils/wfsCache.js: a small TTL'd LRU. Panning around a district
 * re-asks for near-identical bboxes, so rounding the key turns most of those
 * into cache hits and no network at all.
 */
const dateCache = new Map();

function cacheGet(key) {
  const entry = dateCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    dateCache.delete(key);
    return null;
  }
  return entry.data;
}

function cacheSet(key, data) {
  if (dateCache.size >= CACHE_MAX_ENTRIES) {
    dateCache.delete(dateCache.keys().next().value);
  }
  dateCache.set(key, { data, timestamp: Date.now() });
}

const floorTo = (n, step) => Math.floor(n / step) * step;
const ceilTo = (n, step) => Math.ceil(n / step) * step;

/** Round outward so the cached answer always covers the real viewport. */
function roundBbox([w, s, e, n]) {
  return [
    floorTo(w, BBOX_PRECISION),
    floorTo(s, BBOX_PRECISION),
    ceilTo(e, BBOX_PRECISION),
    ceilTo(n, BBOX_PRECISION),
  ];
}

function isoDaysAgo(days) {
  const d = new Date(Date.now() - days * 86400000);
  return d.toISOString();
}

/**
 * One paged STAC sweep over a bounded time window.
 *
 * @returns {{features: Array, truncated: boolean}} `truncated` means the cap
 *   was hit and older scenes exist that this sweep never saw.
 */
async function searchStac({ collection, bbox, from, to, maxPages, signal }) {
  let request = {
    collections: [collection],
    bbox,
    datetime: `${from}/${to}`,
    limit: PAGE_SIZE,
    fields: {
      include: ['properties.datetime', 'properties.eo:cloud_cover'],
      exclude: ['geometry', 'assets', 'links', 'bbox', 'stac_extensions'],
    },
  };

  const features = [];
  let truncated = false;
  for (let page = 0; page < maxPages; page++) {
    const res = await fetch(STAC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error(`STAC search failed: ${res.status}`);
    const body = await res.json();
    features.push(...(body.features || []));

    const next = (body.links || []).find(l => l.rel === 'next');
    if (!next) break;
    // Earth Search returns the continuation as a body to merge into the
    // next POST, not as a bare URL.
    request = { ...request, ...(next.body || {}) };
    if (page === maxPages - 1) truncated = true;
  }
  return { features, truncated };
}

/**
 * Collapse scenes to one entry per acquisition date.
 *
 * A viewport often spans two MGRS tiles, so one date can return several scenes.
 * `cloud` takes the WORST of them on purpose: if half the view is under cloud
 * the date is not usable, and an optimistic average would send the user to a
 * date that renders half-white.
 */
function groupByDate(features) {
  const byDate = new Map();
  for (const f of features) {
    const props = f.properties || {};
    if (!props.datetime) continue;
    const date = props.datetime.slice(0, 10);
    const cloud = typeof props['eo:cloud_cover'] === 'number' ? props['eo:cloud_cover'] : null;
    const existing = byDate.get(date);
    if (!existing) {
      byDate.set(date, { date, datetime: props.datetime, cloud, scenes: 1 });
    } else {
      existing.scenes += 1;
      if (cloud !== null && (existing.cloud === null || cloud > existing.cloud)) {
        existing.cloud = cloud;
      }
      if (props.datetime < existing.datetime) existing.datetime = props.datetime;
    }
  }
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : -1));
}

/**
 * @param {Object}   options
 * @param {number[]} options.bbox     [w, s, e, n] in EPSG:4326, or null
 * @param {boolean}  options.enabled  skip all network when the layer is off
 * @param {number}   options.maxCloud cloud % at or below which a date is "clear"
 * @param {number}   options.lookbackDays
 * @param {string}   options.focusDate  ISO date the caller wants to land on;
 *   guarantees that year is fetched even when the capped sweep stops short
 * @returns {{dates: Array, latestClear: Object|null, loading: boolean, error: Error|null}}
 */
export function useSentinelDates({
  bbox,
  enabled = true,
  maxCloud = DEFAULT_MAX_CLOUD,
  lookbackDays = DEFAULT_LOOKBACK_DAYS,
  collection = DEFAULT_COLLECTION,
  archiveStart = ARCHIVE_START,
  focusDate = null,
} = {}) {
  const [state, setState] = useState({ dates: [], loading: false, error: null });
  const abortRef = useRef(null);
  const latestKeyRef = useRef(null);

  // Exactly the target year, and no wider. Paging runs newest-first, so a
  // window three years wide spends the whole page budget on its newest end
  // and never reaches the year actually asked for (a 2018-2020 window landed
  // in Feb 2020 when the caller wanted mid-2019). One year of one district is
  // ~700 scenes, which fits the budget below with room to spare.
  const focusYear = focusDate ? Number(focusDate.slice(0, 4)) : null;
  const focusFrom = focusYear ? `${focusYear}-01-01T00:00:00Z` : null;
  const focusTo = focusYear ? `${focusYear}-12-31T23:59:59Z` : null;

  const key = bbox && enabled
    ? `${collection}:${roundBbox(bbox).join(',')}:${lookbackDays ?? 'all'}:${focusYear ?? '-'}`
    : null;

  const fetchDates = useCallback(
    async (cacheKey, rounded) => {
      const cached = cacheGet(cacheKey);
      if (cached) {
        setState({ dates: cached, loading: false, error: null });
        return;
      }

      // One request in flight at a time — panning must not stack up queries.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        const from = lookbackDays === null ? archiveStart : isoDaysAgo(lookbackDays);
        const main = await searchStac({
          collection,
          bbox: rounded,
          from,
          to: new Date().toISOString(),
          maxPages: MAX_PAGES,
          signal: controller.signal,
        });

        const features = [...main.features];
        let truncated = main.truncated;

        // The paged sweep is newest-first and capped, so over a busy district
        // it can stop short of an older year the caller actually asked for
        // (Sentinel-2's revisit puts ~5 years inside the cap; the user then
        // clicks "go to 2019" and silently lands in 2021). When the wanted
        // date falls outside what the sweep reached, ask for that year
        // directly — one bounded window is a page or two, not another sweep.
        if (focusFrom && focusTo && truncated) {
          const reached = main.features.reduce(
            (oldest, f) => {
              const dt = f.properties?.datetime;
              return dt && (!oldest || dt < oldest) ? dt : oldest;
            },
            null
          );
          if (!reached || focusTo < reached) {
            const focus = await searchStac({
              collection,
              bbox: rounded,
              from: focusFrom,
              to: focusTo,
              maxPages: FOCUS_MAX_PAGES,
              signal: controller.signal,
            });
            features.push(...focus.features);
          }
        }

        const dates = groupByDate(features);
        dates.truncated = truncated;
        cacheSet(cacheKey, dates);
        // A late response for a viewport we have since left must not win.
        if (latestKeyRef.current !== cacheKey) return;
        setState({ dates, loading: false, error: null });
      } catch (err) {
        if (err.name === 'AbortError') return;
        setState({ dates: [], loading: false, error: err });
      }
    },
    [lookbackDays, collection, archiveStart, focusFrom, focusTo]
  );

  useEffect(() => {
    latestKeyRef.current = key;
    if (!key) {
      setState({ dates: [], loading: false, error: null });
      return;
    }
    fetchDates(key, roundBbox(bbox));
  }, [key, bbox, fetchDates]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const latestClear = state.dates.find(d => d.cloud === null || d.cloud <= maxCloud) || null;

  return { ...state, latestClear };
}
